import {
  Button, Checkbox, FormControlLabel, Link, TextField, Typography,
} from '@mui/material';
import { updateProfile } from 'api/auth';
import RedirectProvider from 'components/RedirectProvider';
import { AuthContext, SnackBarContext } from 'constants/contexts';
import useSentryUpdateEffect from 'hooks/useSentryUpdateEffect';
import React, { useContext, useEffect, useState } from 'react';
import { captureException, getHttpErrorMessage } from 'utils/error';
import { validateEmail } from 'utils/string';
import AuthWrapper from './AuthWrapper';

function SignUp() {
  const [name, setName] = useState();
  const [email, setEmail] = useState();
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useContext(AuthContext);
  const showSnackBar = useContext(SnackBarContext);
  const { redirect } = RedirectProvider.useRedirect();
  const [acceptTerms, setAcceptTerms] = useState(true);

  useEffect(() => {
    if (user) {
      setName(user?.name);
      setEmail(user?.email);
    } else {
      setName('');
      setEmail('');
    }
  }, [user]);

  useSentryUpdateEffect({
    page: 'signup',
    name,
    email,
  }, [name, email]);

  const onSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    updateProfile({ name, email, is_terms_of_service_accepted: acceptTerms })
      .then((res) => {
        redirect();
        setUser({
          customer: res.message?.uuid,
          phone: res.message?.phone,
          email: res.message?.email,
          name: res.message?.name,
        });
        setLoading(false);
      })
      .catch((err) => {
        captureException(err);
        setLoading(false);
        showSnackBar({ message: getHttpErrorMessage(err), type: 'error' });
      });
  };

  return (
    <AuthWrapper
      subTitle="Sign up by entering your details below"
      action={(
        <Button
          size="large"
          fullWidth
          variant="muted"
          onClick={onSubmit}
          disabled={!name || !email || name?.trim()?.length < 3 || !validateEmail(email) || loading || !acceptTerms}
        >
          Get started
        </Button>
      )}
    >

      <TextField
        error={name?.trim()?.length < 3}
        inputProps={{ minLength: 3 }}
        helperText={
          name?.trim()?.length < 3
            ? 'You should at least enter 3 characters as your name'
            : undefined
        }
        required
        placeholder="Please enter your name"
        label="Name"
        fullWidth
        value={name || ''}
        onChange={(e) => setName(e?.target?.value)}
        InputLabelProps={{ shrink: true }}
        disabled={loading}
      />
      <TextField
        error={email && !validateEmail(email)}
        helperText={
          email
            ? validateEmail(email)
              ? undefined
              : 'Please enter a valid email address'
            : undefined
        }
        type="email"
        required
        fullWidth
        placeholder="Please enter your email"
        label="Email"
        value={email || ''}
        onChange={(e) => setEmail(e?.target?.value)}
        autoComplete="email"
        InputLabelProps={{ shrink: true }}
        disabled={loading}
      />
      <FormControlLabel
        control={(
          <Checkbox
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            disabled={loading}
          />
        )}
        sx={{
          '& .MuiCheckbox-root': {
            padding: '0 9px 9px',
          },
          '& .MuiTypography-root': {
            pt: 1,
          },
        }}
        label={(
          <Typography variant="body-md">
            Accepting the
            {' '}
            <Link
              href="https://omnicart.tech/online-ordering-terms"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'underline',
                color: 'text.primary',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Terms of Service
            </Link>
            {' '}
            and
            {' '}
            <Link
              href="https://omnicart.tech/online-ordering-privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'underline',
                color: 'text.primary',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Privacy Policy
            </Link>
          </Typography>
        )}
      />

    </AuthWrapper>
  );
}

export default SignUp;
